/**
 * Auth Helpers - Utilities for robust authentication flow
 * 
 * Provides:
 * - Safe redirect to login with history state preservation
 * - Resilient session checking with retry logic
 * - Cache helpers for optimistic UI
 */

import { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

export interface RedirectState {
  from: string;
  timestamp?: number;
}

/**
 * Safely redirect to login page, preserving the original destination
 * Uses history.replace to avoid stacking history entries
 */
export function safeRedirectToLogin(from?: string): void {
  const currentPath = window.location.pathname + window.location.search;
  const redirectPath = from || currentPath;
  
  // Avoid redirecting if already on login page
  if (redirectPath === '/login' || redirectPath.startsWith('/login')) {
    return;
  }

  const state: RedirectState = {
    from: redirectPath,
    timestamp: Date.now()
  };

  // Use replace to avoid adding to history stack
  window.history.replaceState(null, '', '/login');
  window.location.href = `/login?from=${encodeURIComponent(redirectPath)}`;
}

/**
 * Get the original destination from URL or state
 */
export function getRedirectDestination(): string | null {
  const params = new URLSearchParams(window.location.search);
  const from = params.get('from');
  if (from) {
    return from;
  }
  
  // Try to get from history state
  const state = window.history.state as RedirectState | null;
  if (state?.from) {
    return state.from;
  }
  
  return null;
}

/**
 * Resilient session check with retry logic
 * Returns { session, isValid, error }
 */
export interface SessionCheckResult {
  session: Session | null;
  isValid: boolean;
  error: Error | null;
}

export async function checkSession(
  retries: number = 1,
  retryDelay: number = 500
): Promise<SessionCheckResult> {
  if (!supabase) {
    return {
      session: null,
      isValid: false,
      error: new Error('Supabase client not initialized')
    };
  }

  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        lastError = error;
        // If it's a network error, retry
        if (attempt < retries && error.message?.includes('network')) {
          await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)));
          continue;
        }
        return {
          session: null,
          isValid: false,
          error
        };
      }

      // Check if session is valid (not expired)
      if (session) {
        const expiresAt = session.expires_at;
        if (expiresAt && expiresAt * 1000 < Date.now()) {
          return {
            session: null,
            isValid: false,
            error: new Error('Session expired')
          };
        }
      }

      return {
        session,
        isValid: !!session,
        error: null
      };
    } catch (err) {
      lastError = err as Error;
      if (attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)));
        continue;
      }
    }
  }

  return {
    session: null,
    isValid: false,
    error: lastError || new Error('Session check failed after retries')
  };
}

/**
 * Cache helpers for optimistic UI
 */
const CACHE_PREFIX = 'fiverflow:auth:';
const CACHE_KEYS = {
  USER_META: `${CACHE_PREFIX}userMeta`,
  ROLE: `${CACHE_PREFIX}role`,
  LAST_CHECK: `${CACHE_PREFIX}lastCheck`
} as const;

export interface CachedUserMeta {
  username?: string;
  avatar?: string;
  role?: string;
  userId: string;
  timestamp: number;
}

/**
 * Get cached user metadata
 */
export function getCachedUserMeta(): CachedUserMeta | null {
  try {
    const cached = localStorage.getItem(CACHE_KEYS.USER_META);
    if (!cached) return null;
    
    const meta: CachedUserMeta = JSON.parse(cached);
    // Cache valid for 5 minutes
    if (Date.now() - meta.timestamp > 5 * 60 * 1000) {
      localStorage.removeItem(CACHE_KEYS.USER_META);
      return null;
    }
    return meta;
  } catch {
    return null;
  }
}

/**
 * Set cached user metadata
 */
export function setCachedUserMeta(meta: Partial<CachedUserMeta> & { userId: string }): void {
  try {
    const cached: CachedUserMeta = {
      ...meta,
      timestamp: Date.now()
    } as CachedUserMeta;
    localStorage.setItem(CACHE_KEYS.USER_META, JSON.stringify(cached));
  } catch (err) {
    // Silently fail if storage is unavailable
    console.warn('Failed to cache user meta:', err);
  }
}

/**
 * Get cached role
 */
export function getCachedRole(): string | null {
  try {
    return localStorage.getItem(CACHE_KEYS.ROLE);
  } catch {
    return null;
  }
}

/**
 * Set cached role
 */
export function setCachedRole(role: string): void {
  try {
    localStorage.setItem(CACHE_KEYS.ROLE, role);
  } catch (err) {
    console.warn('Failed to cache role:', err);
  }
}

/**
 * Clear all auth cache
 */
export function clearAuthCache(): void {
  try {
    Object.values(CACHE_KEYS).forEach(key => localStorage.removeItem(key));
  } catch (err) {
    console.warn('Failed to clear auth cache:', err);
  }
}

/**
 * Get last session check timestamp
 */
export function getLastSessionCheck(): number {
  try {
    const timestamp = localStorage.getItem(CACHE_KEYS.LAST_CHECK);
    return timestamp ? parseInt(timestamp, 10) : 0;
  } catch {
    return 0;
  }
}

/**
 * Set last session check timestamp
 */
export function setLastSessionCheck(): void {
  try {
    localStorage.setItem(CACHE_KEYS.LAST_CHECK, Date.now().toString());
  } catch (err) {
    // Silently fail
  }
}

/**
 * Check if we should perform a session check (throttled)
 * Returns true if last check was > 2 minutes ago
 */
export function shouldCheckSession(): boolean {
  const lastCheck = getLastSessionCheck();
  const twoMinutes = 2 * 60 * 1000;
  return Date.now() - lastCheck > twoMinutes;
}

